import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FONT_HEADING } from "./constants";
import logo from "../assets/image/eblackdog_logo.png";

const API_BASE_URL = "https://rfs1.react.eblackdog.net/api";

const G = {
  green:       "#3A9B52",
  greenHover:  "#2E8042",
  greenGlow:   "rgba(58,155,82,0.18)",
  greenBorder: "rgba(58,155,82,0.30)",
  greenFaint:  "rgba(58,155,82,0.10)",
  pageBg:      "#111111",
  sidebarBg:   "#161616",
  sidebarTop:  "#1A1A1A",
  divider:     "#242424",
  textPrimary: "#F3F4F6",
  textSub:     "#9CA3AF",
  textMuted:   "#6B7280",
  red:         "#F87171",
  redFaint:    "rgba(239,68,68,0.08)",
};

// Icons
const IcoChevronDown = ({ open }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
       style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const IcoDot = () => (
  <span style={{ width: 5, height: 5, borderRadius: "50%", background: G.green, display: "inline-block", flexShrink: 0 }} />
);
const IcoLogout = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={G.red} strokeWidth="2.5" strokeLinecap="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);
const IcoMenu = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={G.green} strokeWidth="2.5" strokeLinecap="round">
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);
const IcoPaw = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill={G.green}>
    <ellipse cx="6" cy="6" rx="2.5" ry="3"/>
    <ellipse cx="12" cy="4" rx="2.5" ry="3"/>
    <ellipse cx="18" cy="6" rx="2.5" ry="3"/>
    <ellipse cx="3.5" cy="12" rx="2" ry="2.5"/>
    <path d="M12 22c-4 0-8-3-8-7 0-2 1.5-3.5 3-4.5 1-.7 2.5-1.5 5-1.5s4 .8 5 1.5c1.5 1 3 2.5 3 4.5 0 4-4 7-8 7z"/>
  </svg>
);

const MENU_CONFIG = [
  { key:"dashboard",       title:"Dashboard",          path:"/dashboard",         moduleNames:["Dashboard"] },
  { key:"users",           title:"User Details",        moduleNames:["User Details","Users","User Management"],
    children:[
      { title:"User List",           path:"/user_list",           subNames:["User List"] },
      { title:"Add New User",        path:"/add_user",            subNames:["Add New User"] },
      { title:"Sentinel User List",  path:"/sentinel_user_list",  subNames:["Sentinel User List"] },
      { title:"User Incident Types", path:"/add_incidents",       subNames:["User Incident Types"] },
      { title:"Device Register",     path:"/device_register",     subNames:["Device Register"] },
    ]},
  {
    key: "routes",
    title: "Route Management",
    moduleNames: ["Route Management", "Routes"],
    children: [
      { title: "Create Route", path: "/create_route", subNames: ["Create Route"] },
      { title: "Route List", path: "/route_list", subNames: ["Route List"] },
      { title: "Place List", path: "/place_list", subNames: ["Place List"] },
      { title: "Sentinel Route List", path: "/sentinel_route_list", subNames: ["Sentinel Route List"] },
    ],
  },
  { key:"qr",              title:"QR Codes",           moduleNames:["QR Codes","QR Code Module","QR Code","QR"],
    children:[
      { title:"Patrol QR",   path:"/patrol-qr",  subNames:["Patrol QR"] },
      { title:"Sentinel QR", path:"/sentinel-qr", subNames:["Sentinel QR"] },
    ]},
  { key:"installation",    title:"Installation Module", path:"/installation",      moduleNames:["Installation Module","Installation"] },
  { key:"reports",         title:"Reporting System",   moduleNames:["Reporting System","Reports"],
    children:[
      { title:"Summaries Module", path:"/summaries", subNames:["Summaries Module"] },
      { title:"Analytics Module", path:"/analytics",  subNames:["Analytics Module"] },
      { title:"Export Module",    path:"/export",     subNames:["Export Module"] },
      { title:"Custom Module",    path:"/custom",     subNames:["Custom Module"] },
    ]},
  {
    key: "sentinel_modules",
    title: "Sentinel Modules",
    moduleNames: [
      "Sentinel Modules",
      "Sentinel Dashboard",
      "Sentinel Reports",
      "InCampus Module",
      "InCampus",
    ],
    children: [
      { title: "Sentinel Dashboard", path: "/sentinel_dashboard", subNames: ["Sentinel Dashboard"] },
      { title: "Sentinel Reports", path: "/sentinel_reports", subNames: ["Sentinel Reports"] },
      { title: "InCampus Module", path: "/incampus", subNames: ["InCampus Module"] },
    ],
  },
];

/* =========================================================
   LOGOUT – clear tokens + permanently clear ALL SW caches
========================================================= */
async function clearAppCaches() {
  // 1. Tell the service worker to delete every cache
  if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: "CLEAR_CACHE" });
  }

  // 2. Also clear from the page itself
  if ("caches" in window) {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  }
}

async function handleLogout() {
  // Clear all auth / session data
  localStorage.removeItem("token");
  localStorage.removeItem("authToken");
  localStorage.removeItem("adminToken");
  localStorage.removeItem("guardToken");
  localStorage.removeItem("isLoggedIn");
  localStorage.removeItem("isGuardLoggedIn");
  localStorage.removeItem("userData");
  localStorage.removeItem("guardData");
  localStorage.removeItem("memberId");
  localStorage.removeItem("guardLoginType");
  localStorage.removeItem("company_name");
  localStorage.removeItem("licensedFor");
  localStorage.removeItem("admin_active");
  localStorage.removeItem("company_address");
  localStorage.removeItem("company_phone");
  localStorage.removeItem("admin_email");
  localStorage.removeItem("company_logo");
  localStorage.removeItem("user_map");
  localStorage.removeItem("admin_id");
  localStorage.removeItem("client_id");
  localStorage.removeItem("skinTheme");

  // Permanently clear all service worker caches
  await clearAppCaches();

  // Optional: unregister the service worker completely
  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((reg) => reg.unregister()));
  }

  // Hard redirect so nothing stays in memory
  window.location.replace("/");
}

export default function Sidebar({ skin, sidebarOpen, setSidebarOpen, routePage, onLogout }) {
  const location = useLocation();
  const currentPath = location.pathname;
  const [openMenu, setOpenMenu] = useState("");
  const [permissionRows, setPermissionRows] = useState([]);
  const [permissionLoading, setPermissionLoading] = useState(false);

  const token =
    localStorage.getItem("guardToken") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token");

  const guardData = (() => {
    try {
      return JSON.parse(localStorage.getItem("guardData") || "{}");
    } catch {
      return {};
    }
  })();

  const userData = (() => {
    try {
      return JSON.parse(localStorage.getItem("userData") || "{}");
    } catch {
      return {};
    }
  })();

  const memberId =
    guardData.memberId ||
    guardData.member_id ||
    guardData.fld_memberId ||
    guardData.guardId ||
    localStorage.getItem("memberId") ||
    userData.memberId ||
    userData.member_id ||
    userData.fld_memberId ||
    "";

  const loginType =
    localStorage.getItem("guardLoginType") ||
    guardData.loginType ||
    userData.loginType ||
    "";

  const isGuard =
    localStorage.getItem("isGuardLoggedIn") === "true" ||
    loginType === "patrol" ||
    loginType === "sentinel";

  // Anyone with a token who isn't a guard is treated as an admin session.
  const isAdmin = !isGuard && Boolean(token);

  const normalize = (v) =>
    String(v || "")
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ");

  const normalizeLink = (v) => String(v || "").trim().toLowerCase();

  const yes = (v) =>
    v === true ||
    v === 1 ||
    v === "1" ||
    normalize(v) === "true" ||
    normalize(v) === "yes";

  // --- Guard permissions fetch ---
  useEffect(() => {
    if (!isGuard || !memberId) return;

    (async () => {
      try {
        setPermissionLoading(true);
        const res = await fetch(
          `https://rfs1.react.eblackdog.net/api/guard-permissions/sidebar/${encodeURIComponent(memberId)}`,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const result = await res.json().catch(() => ({}));
        console.log("response", result);
        setPermissionRows(
          result.success ? result.data || result.permissions || [] : []
        );
      } catch (e) {
        console.error(e);
        setPermissionRows([]);
      } finally {
        setPermissionLoading(false);
      }
    })();
  }, [isGuard, memberId, token]);

  // --- Admin permissions fetch ---
  useEffect(() => {
    if (!isAdmin) return;

    (async () => {
      try {
        setPermissionLoading(true);
        const res = await fetch(
          `https://rfs1.react.eblackdog.net/api/guard-permissions/sidebar/admin`,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const result = await res.json().catch(() => ({}));
        console.log("admin permissions response", result);
        setPermissionRows(result.success ? result.data || [] : []);
      } catch (e) {
        console.error(e);
        setPermissionRows([]);
      } finally {
        setPermissionLoading(false);
      }
    })();
  }, [isAdmin, token]);

  // --- Guard-style matching ---
  const hasModPerm = (item) => {
    return permissionRows.some((row) => {
      const dep = normalize(row.fld_department || row.department);
      const mod = normalize(row.modules || row.module || row.title);
      return (
        yes(row.fld_view ?? row.view ?? row.approve) &&
        item.moduleNames.some((n) => {
          const t = normalize(n);
          return dep === t || mod === t;
        })
      );
    });
  };

  const hasSubPerm = (child, parent) => {
    return permissionRows.some((row) => {
      const dep = normalize(row.fld_department || row.department);
      const mod = normalize(row.modules || row.module || row.title);
      const sub = normalize(
        row.fld_submodules || row.submodules || row.submodule
      );
      return (
        yes(row.fld_view ?? row.view ?? row.approve) &&
        parent.moduleNames.some((n) => {
          const t = normalize(n);
          return dep === t || mod === t;
        }) &&
        child.subNames.some((n) => normalize(n) === sub)
      );
    });
  };

  // --- Admin-style matching ---
  const hasCategoryPerm = (item) => {
    return permissionRows.some((row) => {
      if (!yes(row.approve)) return false;
      const mod = normalize(row.modules);
      return item.moduleNames.some((n) => normalize(n) === mod);
    });
  };

  const hasLinkPerm = (path) => {
    return permissionRows.some(
      (row) =>
        yes(row.approve) &&
        row.links &&
        normalizeLink(row.links) === normalizeLink(path)
    );
  };

  const visibleMenu = useMemo(() => {
    if (isGuard) {
      return MENU_CONFIG.map((item) => {
        if (!hasModPerm(item)) return null;
        if (!item.children) return item;
        const kids = item.children.filter((c) => hasSubPerm(c, item));
        return kids.length ? { ...item, children: kids } : null;
      }).filter(Boolean);
    }

    if (isAdmin) {
      return MENU_CONFIG.map((item) => {
        if (!item.children) {
          const ok = hasLinkPerm(item.path) || hasCategoryPerm(item);
          return ok ? item : null;
        }

        const leafRowsExist = item.children.some((c) =>
          permissionRows.some(
            (row) =>
              row.links &&
              normalizeLink(row.links) === normalizeLink(c.path)
          )
        );

        let kids;
        if (leafRowsExist) {
          kids = item.children.filter((c) => hasLinkPerm(c.path));
        } else if (hasCategoryPerm(item)) {
          kids = item.children;
        } else {
          kids = [];
        }

        return kids.length ? { ...item, children: kids } : null;
      }).filter(Boolean);
    }

    return MENU_CONFIG;
  }, [isGuard, isAdmin, permissionRows]);

  const getMenuFromPath = (path) => {
    const np = normalize(path);
    for (const item of visibleMenu) {
      if (normalize(item.path) === np) return item.key;
      if (item.children?.some((c) => normalize(c.path) === np)) return item.key;
    }
    return "";
  };

  useEffect(() => {
    const m = getMenuFromPath(currentPath);
    if (m) setOpenMenu(m);
  }, [currentPath, visibleMenu]);

  const isPathActive = (p) => normalize(currentPath) === normalize(p);
  const isMenuActive = (k) => getMenuFromPath(currentPath) === k;

  // Prefer parent onLogout if provided, otherwise use built-in logout
  const doLogout = typeof onLogout === "function" ? onLogout : handleLogout;

  return (
    <>
      <style>{`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  .sb-root {
    position: fixed; top: 0; bottom: 0; left: 0; width: 270px;
    background: var(--sidebar-bg);
    border-right: 1px solid var(--divider);
    display: flex; flex-direction: column; z-index: 1000;
    font-family: 'Inter', system-ui, sans-serif;
    transition: transform 0.28s cubic-bezier(.4,0,.2,1);
  }
  .sb-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.55); z-index: 999; display: none; }
  @media(max-width:1024px){
    .sb-root{ transform: translateX(-100%); }
    .sb-root.open{ transform: translateX(0); }
    .sb-overlay.open{ display:block; }
  }

  .sb-top { background: var(--sidebar-top); border-bottom: 3px solid var(--accent); padding: 20px 20px 18px; flex-shrink: 0; }
  .sb-brand-row { display: flex; align-items: center; gap: 12px; }
  .sb-logo-wrap {
    width: 52px; height: 52px; border-radius: 12px;
    background: var(--card-bg); border: 2px solid var(--accent);
    box-shadow: 0 0 0 3px var(--accent-glow);
    overflow: hidden; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
  }
  .sb-logo-wrap img { width:100%; height:100%; object-fit:contain; }
  .sb-brand-name { font-size: 13px; font-weight: 800; color: var(--text-primary); letter-spacing: -0.01em; line-height: 1.3; margin: 0; }
  .sb-brand-tag {
    display: inline-flex; align-items: center; gap: 5px; margin-top: 6px;
    background: var(--accent-faint); border: 1px solid var(--accent-border);
    padding: 3px 9px; border-radius: 20px;
    font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--accent);
  }

  .sb-nav { flex: 1; overflow-y: auto; padding: 10px 0; }
  .sb-nav::-webkit-scrollbar { width: 4px; }
  .sb-nav::-webkit-scrollbar-track { background: transparent; }
  .sb-nav::-webkit-scrollbar-thumb { background: var(--card-border); border-radius: 4px; }

  .sb-item {
    display: flex; align-items: center; justify-content: space-between; width: 100%;
    padding: 11px 18px 11px 16px; font-size: 13.5px; font-weight: 500;
    color: var(--text-sub); background: none; border: none; border-left: 3px solid transparent;
    cursor: pointer; text-decoration: none;
    transition: background 0.14s, color 0.14s, border-color 0.14s;
    text-align: left; box-sizing: border-box;
  }
  .sb-item:hover { background: var(--accent-faint); color: var(--text-primary); border-left-color: var(--accent-border); }
  .sb-item.active { background: var(--accent-faint); color: var(--text-primary); border-left-color: var(--accent); font-weight: 700; }

  .sb-sub { background: rgba(0,0,0,0.04); }
  .sb-sub-item {
    display: flex; align-items: center; gap: 10px; width: 100%;
    padding: 9px 18px 9px 32px; font-size: 13px; font-weight: 500;
    color: var(--text-muted); border-left: 3px solid transparent; text-decoration: none;
    transition: background 0.14s, color 0.14s, border-color 0.14s; box-sizing: border-box;
  }
  .sb-sub-item:hover { background: var(--accent-faint); color: var(--text-primary); border-left-color: var(--accent-border); }
  .sb-sub-item.active { background: var(--accent-faint); color: var(--accent); border-left-color: var(--accent); font-weight: 600; }

  .sb-section-label { padding: 14px 18px 5px; font-size: 9.5px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--text-faint); }

  .sb-footer { border-top: 1px solid var(--divider); flex-shrink: 0; }
  .sb-logout {
    display: flex; align-items: center; gap: 10px; width: 100%; padding: 14px 18px;
    background: none; border: none; cursor: pointer;
    font-size: 13.5px; font-weight: 600; color: var(--red);
    transition: background 0.14s; text-align: left;
  }
  .sb-logout:hover { background: var(--red-faint); }

  .sb-ham {
    display: none; position: fixed; left: 14px; top: 13px; z-index: 1200;
    width: 34px; height: 34px; align-items: center; justify-content: center;
    border-radius: 10px; background: var(--accent-faint); border: 1px solid var(--accent-border);
    cursor: pointer; color: var(--accent);
  }
  @media(max-width:1024px){ .sb-ham{ display:flex; } }
`}</style>

      {/* Mobile hamburger */}
      <button className="sb-ham" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
        <IcoMenu />
      </button>

      {/* Overlay */}
      <div
        className={`sb-overlay ${sidebarOpen ? "open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`sb-root ${sidebarOpen ? "open" : ""}`}>
        {/* Brand top */}
        <div className="sb-top">
          <div className="sb-brand-row">
            <div className="sb-logo-wrap">
              <img src={logo} alt="eBLACKDOG Logo" />
            </div>
            <div>
              <p className="sb-brand-name">
                Eblackdog Security
                <br />
                Patrol & Sentinel
              </p>
              <div className="sb-brand-tag">
                <IcoPaw />
                Command Centre
              </div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="sb-nav">
          {permissionLoading && (
            <p className="sb-section-label" style={{ color: G.textMuted }}>
              Loading menu...
            </p>
          )}

          {!permissionLoading &&
            visibleMenu.map((item, idx) => {
              const isFirst = idx === 0;

              if (item.children?.length) {
                const active = isMenuActive(item.key);
                return (
                  <div key={item.key}>
                    {isFirst && <p className="sb-section-label">Navigation</p>}
                    <button
                      type="button"
                      className={`sb-item ${active ? "active" : ""}`}
                      onClick={() =>
                        setOpenMenu((p) => (p === item.key ? "" : item.key))
                      }
                    >
                      <span>{item.title}</span>
                      <IcoChevronDown open={openMenu === item.key} />
                    </button>

                    {openMenu === item.key && (
                      <div className="sb-sub">
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={() => setSidebarOpen(false)}
                            className={`sb-sub-item ${
                              isPathActive(child.path) ? "active" : ""
                            }`}
                          >
                            <IcoDot />
                            {child.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div key={item.key}>
                  {isFirst && <p className="sb-section-label">Navigation</p>}
                  <Link
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`sb-item ${
                      isPathActive(item.path) ? "active" : ""
                    }`}
                  >
                    {item.title}
                  </Link>
                </div>
              );
            })}

          {!permissionLoading &&
            (isGuard || isAdmin) &&
            visibleMenu.length === 0 && (
              <p style={{ padding: "12px 18px", fontSize: 13, color: G.red }}>
                No menu permission assigned
              </p>
            )}
        </nav>

        {/* Footer – Sign out */}
        <div className="sb-footer">
          <button className="sb-logout" type="button" onClick={doLogout}>
            <IcoLogout />
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
}